import { useState } from 'react'
import { api } from '../../utils/api'
import { FORM_STYLE, ERROR_STYLE, SUCCESS_STYLE } from './orderEditStyles'

/**
 * OrderTrackingEdit: the tracking row of the Shipping section, with an Edit
 * button on a shipped order. Editing turns the row into a form. Prism changes
 * the number on the marketplace and writes the order note; its message says
 * what happened (customer emailed, or "change it in eBay too").
 *
 *   fulfillment   the shipped parcel { tracking_number, tracking_company, tracking_url }
 *   carriers      the operator carrier list, in display order
 *   canEdit       true once the order is shipped
 *   onSaved       (result) => void, called after Prism accepted the change
 */

const TRACKING_MAX_LENGTH = 64
const NO_CARRIER_CHANGE = ''
const NOT_UPDATED_WARNING = {
  ebay: "eBay won't be updated: change it in eBay too",
  reverb: "Reverb won't be updated: change it in Reverb too"
}

const ROW_STYLE = {
  gridColumn: '1 / -1',
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 'var(--space-3)'
}
const NUMBER_BOX_STYLE = {
  flex: 1,
  minWidth: 0,
  padding: '6px 8px',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  background: '#fff',
  fontSize: '0.75rem',
  overflowWrap: 'anywhere'
}
export default function OrderTrackingEdit({ marketplace, orderNumber, fulfillment, carriers, canEdit, onSaved }) {
  const currentNumber = fulfillment?.tracking_number || ''
  const currentCarrier = fulfillment?.tracking_company || ''
  const carrierKnown = carriers.includes(currentCarrier)
  const isShopify = marketplace === 'shopify'

  const [editing, setEditing] = useState(false)
  const [number, setNumber] = useState('')
  const [carrier, setCarrier] = useState(NO_CARRIER_CHANGE)
  const [notify, setNotify] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [message, setMessage] = useState(null)

  const trimmed = number.trim()
  const canSave = trimmed.length > 0 && !/\s/.test(trimmed) && !busy

  function openForm() {
    setNumber(currentNumber)
    setCarrier(carrierKnown ? currentCarrier : NO_CARRIER_CHANGE)
    setNotify(true)
    setError(null)
    setMessage(null)
    setEditing(true)
  }

  function requestBody() {
    const body = { tracking_number: trimmed, notify_customer: isShopify && notify }
    if (carrier !== NO_CARRIER_CHANGE) body.carrier = carrier
    if (currentNumber) body.old_tracking_number = currentNumber
    return body
  }

  async function save(e) {
    e.preventDefault()
    if (!canSave) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.put(`/api/orders/${marketplace}/${orderNumber}/tracking`, requestBody())
      setMessage(result?.message || 'Tracking updated')
      setEditing(false)
      onSaved(result)
    } catch (err) {
      setError(err.message || 'Could not change the tracking')
    } finally {
      setBusy(false)
    }
  }

  if (editing) {
    return (
      <div style={ROW_STYLE}>
        <form onSubmit={save} style={FORM_STYLE}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginBottom: 12 }}>
            <div>
              <label htmlFor="tracking-number" className="filter-label">Tracking number</label>
              <input
                id="tracking-number"
                className="form-input"
                value={number}
                maxLength={TRACKING_MAX_LENGTH}
                onChange={(e) => setNumber(e.target.value)}
                disabled={busy}
                autoFocus
              />
            </div>
            <div>
              <label htmlFor="tracking-carrier" className="filter-label">Tracking company</label>
              <select id="tracking-carrier" className="form-input" value={carrier} onChange={(e) => setCarrier(e.target.value)} disabled={busy}>
                {!carrierKnown && <option value={NO_CARRIER_CHANGE}>Keep {currentCarrier || 'current carrier'}</option>}
                {carriers.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </div>
          </div>
          {isShopify ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9em' }}>
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} disabled={busy} />
              Email the customer the new tracking
            </label>
          ) : (
            <div style={{ fontSize: '0.9em', color: 'var(--color-text-muted)' }}>{NOT_UPDATED_WARNING[marketplace]}</div>
          )}
          {error && <div role="alert" style={{ ...ERROR_STYLE, marginTop: 12 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={!canSave}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    )
  }

  return (
    <div style={ROW_STYLE}>
      <div className="order-detail-item">
        <span className="order-detail-label">Tracking Number</span>
        {currentNumber ? (
          <span className="order-detail-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={NUMBER_BOX_STYLE}>{currentNumber}</span>
            {canEdit && (
              <button type="button" className="btn btn-secondary btn-sm" style={{ flex: 'none' }} onClick={openForm}>Edit</button>
            )}
          </span>
        ) : (
          <span className="order-detail-value">—</span>
        )}
      </div>
      <div className="order-detail-item">
        <span className="order-detail-label">Tracking Company</span>
        <span className="order-detail-value">{currentCarrier || '—'}</span>
      </div>
      <div className="order-detail-item">
        <span className="order-detail-label">Tracking URL</span>
        <span className="order-detail-value">
          {fulfillment?.tracking_url ? (
            <a href={fulfillment.tracking_url} target="_blank" rel="noopener noreferrer" className="link">View Tracking</a>
          ) : '—'}
        </span>
      </div>
      {message && <div role="status" style={SUCCESS_STYLE}>{message}</div>}
    </div>
  )
}
